import { create } from 'zustand'
import { apiFetch } from '@/utils/api'

export type Unit = { id: string; name: string; active?: boolean; color?: string }
export type RoleKey = 'owner' | 'admin_rede' | 'gerente_unidade' | 'supervisor' | 'employee' | 'atendente_expedicao' | 'visitante'

export const ALL_UNITS_SENTINEL = '__all__'

type UnitState = {
  units: Unit[]
  unitsLoaded: boolean
  selectedUnitId: string | null
  rolesByUnit: Record<string, string[]>
  loadUnits: () => Promise<void>
  selectUnit: (unitId: string | null) => void
  loadRoles: (unitId: string) => Promise<void>
  createUnit: (name: string, color?: string) => Promise<void>
  updateUnit: (id: string, data: Partial<Unit>) => Promise<void>
}

const LS_KEY = 'sq:selectedUnitId'

function readInitialSelectedUnitId(): string | null {
  const raw = localStorage.getItem(LS_KEY)
  if (raw === ALL_UNITS_SENTINEL) return null
  return raw  // string UUID, or null if never set
}

export const useUnitStore = create<UnitState>((set, get) => ({
  units: [],
  unitsLoaded: false,
  selectedUnitId: readInitialSelectedUnitId(),
  rolesByUnit: {},

  loadUnits: async () => {
    const units = await apiFetch<Unit[]>('/api/units')
    set({ units, unitsLoaded: true })
    // Respect user's prior "All units" choice
    const raw = localStorage.getItem(LS_KEY)
    if (raw === ALL_UNITS_SENTINEL) {
      set({ selectedUnitId: null })
      return
    }
    const current = get().selectedUnitId
    if (current && units.some((u) => u.id === current)) return
    const first = units[0]?.id ?? null
    set({ selectedUnitId: first })
    if (first) localStorage.setItem(LS_KEY, first)
  },

  selectUnit: (unitId: string | null) => {
    set({ selectedUnitId: unitId })
    if (unitId === null) {
      localStorage.setItem(LS_KEY, ALL_UNITS_SENTINEL)
    } else {
      localStorage.setItem(LS_KEY, unitId)
    }
  },

  loadRoles: async (unitId: string) => {
    if (get().rolesByUnit[unitId]) return
    const roles = await apiFetch<string[]>(`/api/units/${unitId}/roles`)
    set((s) => ({ rolesByUnit: { ...s.rolesByUnit, [unitId]: roles } }))
  },

  createUnit: async (name: string, color?: string) => {
    await apiFetch('/api/units', {
      method: 'POST',
      csrf: true,
      body: JSON.stringify({ name, color }),
    })
    await get().loadUnits()
  },

  updateUnit: async (id: string, data: Partial<Unit>) => {
    await apiFetch(`/api/units/${id}`, {
      method: 'PUT',
      csrf: true,
      body: JSON.stringify(data),
    })
    await get().loadUnits()
  },
}))

